import { PaymentStatus, TransactionStatus, TransactionType } from '@seapoint/server/enums';
import { Dot } from 'lucide-react';
import { CopyIcon } from '../../../../../design-system/copy-icon/copy-icon';
import { Inline } from '../../../../../design-system/inline/inline';
import { NameWithLogo } from '../../../../../design-system/name-with-logo/name-with-logo';
import { Stack } from '../../../../../design-system/stack/stack';
import { Tooltip } from '../../../../../design-system/tooltip/tooltip';
import { getTransactionTypeDescription } from '../../../../../utils/get-transaction-type-description';
import { PaymentTimeline, TimelineEvent } from './payment-timeline';

type PaymentInformationProps = {
  type: TransactionType;
  status: string;
  reference: string;
  transactionId: string;
  description?: string;
  cardNumber?: string;
  cardHolderName?: string;
  /** Events from the transaction for timeline display */
  events?: TimelineEvent[];
  /** Payment status for determining timeline state */
  paymentStatus?: PaymentStatus;
  account: {
    name: string;
    currency: string;
    imageUrl: string;
  };
};

type InfoRowData = {
  label: string;
  value: string | React.ReactNode;
};

const InfoRow = ({ label, value }: InfoRowData) => (
  <Inline justify="between" align="center" className="h-8 w-full text-sm leading-[22px] font-thin">
    <div className="text-muted-foreground">{label}</div>
    <div className="text-right">{value}</div>
  </Inline>
);

export const PaymentInformation = ({
  status,
  type,
  reference,
  transactionId,
  description,
  cardNumber,
  cardHolderName,
  events,
  paymentStatus,
  account,
}: PaymentInformationProps) => {
  // Show timeline if we have events
  const hasTimeline = events && events.length > 0;

  const isCardPayment =
    type === TransactionType.CARD_PAYMENT ||
    type === TransactionType.CARD_CHARGEBACK ||
    type === TransactionType.CARD_REFUND;

  const getDescriptionRow = (): InfoRowData => {
    return {
      label: 'Description',
      value: (
        <Tooltip
          trigger={<p className="max-w-full truncate text-right text-sm leading-[22px] font-thin">{description}</p>}
          content={description}
          delayDuration={300}
        />
      ),
    };
  };

  const getPaymentRows = (): InfoRowData[] => {
    if (isCardPayment) {
      return [
        getDescriptionRow(),
        { label: 'Status', value: getStatusLabel(status) },
        {
          label: 'Transaction ID',
          value: <TransactionIdRow transactionId={transactionId} />,
        },
        { label: 'Payment Method', value: getTransactionTypeDescription(type) },
        ...(cardNumber ? [{ label: 'Card Number', value: formatCardNumber(cardNumber) }] : []),
        ...(cardHolderName
          ? [{ label: 'Card Holder Name', value: cardHolderName }]
          : [
              {
                label: 'From',
                value: (
                  <NameWithLogo name={`${account.name} (${account.currency})`} logo={account.imageUrl} logoSize="lg" />
                ),
              },
            ]),
      ];
    }

    if (type === TransactionType.DIRECT_DEBIT) {
      return [
        getDescriptionRow(),
        { label: 'Status', value: getStatusLabel(status) },
        {
          label: 'Transaction ID',
          value: <TransactionIdRow transactionId={transactionId} />,
        },
        { label: 'Payment Method', value: getTransactionTypeDescription(type) },
        {
          label: 'From',
          value: <NameWithLogo name={`${account.name} (${account.currency})`} logo={account.imageUrl} logoSize="lg" />,
        },
      ];
    }

    return [
      { label: 'Status', value: getStatusLabel(status) },
      { label: 'Reference', value: <span className="truncate">{reference}</span> },
      {
        label: 'Transaction ID',
        value: <TransactionIdRow transactionId={transactionId} />,
      },
      { label: 'Payment Method', value: getTransactionTypeDescription(type) },
      {
        label: 'From',
        value: <NameWithLogo name={`${account.name} (${account.currency})`} logo={account.imageUrl} logoSize="lg" />,
      },
    ];
  };

  return (
    <Stack space={4}>
      <div className="text-base leading-[22px] font-medium tracking-[-0.02em]">Payment Information</div>
      {getPaymentRows().map((row, index) => (
        <InfoRow key={index} label={row.label} value={row.value} />
      ))}
      {hasTimeline && paymentStatus && (
        <>
          <div className="h-px w-full bg-border" />
          <PaymentTimeline currentStatus={paymentStatus} events={events} />
        </>
      )}
    </Stack>
  );
};

const formatCardNumber = (cardNumber: string): string => {
  const lastFour = cardNumber.slice(-4);
  return `**** **** **** ${lastFour}`;
};

const getStatusLabel = (status: string) => {
  const statusConfig = {
    [TransactionStatus.FAILED]: {
      color: '#FF531D',
      label: 'Failed',
    },
    [TransactionStatus.BOOKED]: {
      color: '#82CC69',
      label: 'Completed',
    },
    [TransactionStatus.PENDING]: {
      color: '#86868B',
      label: 'Pending',
    },
    [TransactionStatus.CANCELLED]: {
      color: '#86868B',
      label: 'Cancelled',
    },
    [TransactionStatus.FULL_REVERSAL]: {
      color: '#86868B',
      label: 'Reverted',
    },
  };

  const config = statusConfig[status as TransactionStatus] || statusConfig[TransactionStatus.PENDING];

  return (
    <Inline align="center" space={1}>
      <Dot color={config.color} size={18} strokeWidth={3} />
      <p className="text-sm leading-[22px] font-thin">{config.label}</p>
    </Inline>
  );
};

const TransactionIdRow = ({ transactionId }: { transactionId: string }) => {
  return (
    <Inline align="center" space={1} className="h-full">
      <CopyIcon textToCopy={transactionId} />
      <p className="text-sm leading-[22px] font-thin">{transactionId}</p>
    </Inline>
  );
};
